import {BetLineEvent} from '@nolimitcity/slot-game/bin/core/betline/event/BetLineEvent';
import {EventHandler} from '@nolimitcity/slot-game/bin/core/event/EventHandler';
import {GameEvent} from '@nolimitcity/slot-game/bin/core/event/GameEvent';
import {Reels} from '@nolimitcity/slot-game/bin/core/reel/reelarea/Reels';
import {ReelSymbolName} from '@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName';
import {StateReelSymbol} from '@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol';
import {ScreenEvent} from '@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent';
import {IScreenConfig, ScreenController} from '@nolimitcity/slot-game/bin/core/screen/ScreenController';
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageEvent} from '@nolimitcity/slot-game/bin/core/stage/event/StageEvent';
import {ArrayHelper} from '@nolimitcity/slot-game/bin/core/utils/ArrayHelper';
import {Helper} from '@nolimitcity/slot-game/bin/core/utils/Helper';
import {MathHelper} from '@nolimitcity/slot-game/bin/core/utils/MathHelper';
import {NudgeReelEvent} from '@nolimitcity/slot-game/bin/game/reel/nudgereel/NudgeReelEvent';
import {NudgeSpinReelController} from '@nolimitcity/slot-game/bin/game/reel/nudgereel/NudgeSpinReelController';
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {Elastic, TimelineLite} from 'gsap';
import {FreespinsLeftIndicator} from '../../freespinsleft/FreespinsLeftIndicator';
import {BarbarianFuryReelSymbol} from '../../symbols/BarbarianFuryReelSymbol';
import {BFNudgeStackedWPController} from '../../winpresentation/nudgeStacked/BFNudgeStackedWPController';
import {BarbarianFuryGameConfig} from './../../BarbarianFuryGameConfig';
import {BarbarianFurySoundConfig} from './../../BarbarianFurySoundConfig';
import {BarbarianFuryGameMode} from './../../gamemode/BarbarianFuryGameMode';
import {BarbarianFuryParsedGameData} from './../../server/data/BarbarianFuryParsedGameData';
import {BFScatterNudgeView} from './BFScatterNudgeView';

export class BFScatterNudgeController extends ScreenController {
    private readonly NUDGE_STEP_SPEED:number = 0.5;
    public static _ScatterScreenStarted:boolean = false;     // added this to fix #180
    private _nudgeData:BarbarianFuryParsedGameData | null;
    private _nudgeState:boolean[];
    private _isNudging:boolean = false;
    private _scatterPositions:number[] | undefined;
    private _currentScatterIndexes:number[];

    private _currentMode:string;
    private _nextMode:string;
    private _fsLeft:number;

    constructor(name:string, config:IScreenConfig) {
        super(name, config);
        this.initValues();
    }

    protected addFeatureEventHandlers():void {
        EventHandler.addEventListener(this, NudgeReelEvent.NUDGE_STEP_COMPLETED, (event:GameEvent) => this.onNudgeStepCompleted(event.params[0]));
        // The NUDGE_COMPLETE event is shared by different nudge features, have to make sure they are not conflict.
        EventHandler.addEventListener(this, NudgeReelEvent.NUDGE_COMPLETED, (event:GameEvent) => this.onNudgeCompleted(event.params[0]));
        EventHandler.addEventListener(this, NudgeReelEvent.NUDGE_STEP_START, (event:GameEvent) => this.playScatterStepSound());
    }

    protected calculateIsTriggered(data:BarbarianFuryParsedGameData):boolean {
        return (data.doScatterNudge && data.doScatterNudge.indexOf(true) > -1);
    }

    private dimAllOtherSymbols(isAbort:boolean):void {
        //symbols 0-9 on the reel are dimmed out, including the ones on top and the bottom of the visible ones for nudge
        for(let i:number = 0; i < BarbarianFuryGameConfig.instance.REELS_NUM; i++) {
            if(!BFNudgeStackedWPController._nudgeData!.initialStackOverlay[i]) { // added this check to not to dim down stack symbol in fs.
                Reels.getReel(i)!.allSymbols.forEach((symbol:StateReelSymbol) => {
                    if(symbol && symbol.symName !== ReelSymbolName.SCATTER) {
                        (<BarbarianFuryReelSymbol>symbol).changeState({
                            state : BarbarianFuryGameConfig.instance.SYMBOL_STATES.noWin!,
                            fadeInDuration : isAbort ? 0 : 1
                        });
                    }
                });
            }
        }
    }

    private doScatterReelNudge(reelId:number, isAbort:boolean, delay:number):void {
        this._scatterNudgeCOunter = 1;
        if(!isAbort) {
            this._nudgeState[reelId] = true;
        }
        const index:number = this._scatterPositions![reelId];
        if(MathHelper.isInRange(index, 0, 3)) {
            if(isAbort) {
                (<NudgeSpinReelController>Reels.getReel(reelId)).abortNudge(4 - index);
            } else {
                this._currentScatterIndexes[reelId] = index;
                (<NudgeSpinReelController>Reels.getReel(reelId)).nudge(4 - index, this.NUDGE_STEP_SPEED, Elastic.easeIn.config(1, 1), delay);
            }
        }
    }

    private initValues():void {
        this._nudgeState = ArrayHelper.initArrayWithValues<boolean>(5, () => {
            return false;
        });
        this._currentScatterIndexes = ArrayHelper.initArrayWithValues<number>(5, () => {
            return -1
        });
    }

    /**
     * @override Overridding it to make sure showKeypad() is not called.
     */
    protected onCloseComplete():void {
        Logger.logDev("Close Screen " + this.moduleName);
        SlotGame.winFieldController.showWinField();
        EventHandler.dispatchEvent(new GameEvent(StageEvent.WANT_RESIZE));
        EventHandler.dispatchEvent(new GameEvent(BetLineEvent.SET_ENABLED, true));
        EventHandler.dispatchEvent(new GameEvent(ScreenEvent.SCREEN_CLOSED));
    }

    private onNudgeStepCompleted(reelId:number):void {
        if(this._nudgeState[reelId]) {
            if(this._nudgeData!.mode === BarbarianFuryGameMode.FREESPIN) {
                EventHandler.dispatchEvent(new GameEvent(FreespinsLeftIndicator.UPDATE_FS_NUMBER));
                // EventHandler.dispatchEvent(new GameEvent(KeypadEvent.UPDATE_FREESPINS_LEFT));
            }
            // SlotGame.sound.playEffect(WildNudgeSoundConfig.instance.SCATTER_NUDGE);
            let positions:[number, number] = Helper.getSymbolPositions(reelId, this._currentScatterIndexes[reelId]);
            (this._view as BFScatterNudgeView).showAddFreespin(positions);
            this._currentScatterIndexes[reelId]++;
        }
    }

    private _scatterNudgeCOunter:number = 1;

    private playScatterStepSound() {
        if(!this._isNudging) {
            return
        }
        if(this._scatterNudgeCOunter > 3) {
            this._scatterNudgeCOunter = 1;
        }
        SlotGame.sound.playEffect(BarbarianFurySoundConfig.instance.SCATTER_NUDGE + this._scatterNudgeCOunter);
        this._scatterNudgeCOunter++;
    }

    private onNudgeCompleted(reelId:number):void {
        if(!this._isNudging) {
            return;
        }
        // reset the symbol to turn off fire.
        const scatter:StateReelSymbol | null = Reels.getSymbol(reelId, this._currentScatterIndexes[reelId]);
        if(scatter) {
            scatter.changeState({
                state : BarbarianFuryGameConfig.instance.SYMBOL_STATES.normal,
                fadeInDuration : 0,
                replay : false
            });
        }

        this._nudgeState[reelId] = false;
        this._currentScatterIndexes[reelId] = -1;
        //Logger.logDev(`NudgeScatterWPController.onNudgeCompleted(reelId:${reelId}): nudgeState : ${this._nudgeState.toString()}`);
        if(this._nudgeState.indexOf(true) > -1) {
            return;
        }

        this._isNudging = false;

        const timer:TimelineLite = new TimelineLite();
        timer.add(() => {
            // added this to trigger FS intro in normal game flow and making sure intro do not gets triggered on retrigger.
            // once we fire the following event screen will get close due to FS intro, no need to call onCloseComplete of its own module.
            if(this._currentMode === BarbarianFuryGameMode.NORMAL && this._nextMode === BarbarianFuryGameMode.FREESPIN) {
                EventHandler.dispatchEvent(new GameEvent("START_FREE_SPIN_INTRO"));
            } else {
                this.onCloseComplete();
            }
        }, 0.5);
    }

    protected parseFeatureGameData(data:BarbarianFuryParsedGameData):void {
        this._nudgeData = data;
        this._nextMode = data.nextMode;
        this._currentMode = data.mode;
        this._scatterPositions = data.scatterPositions;
        this._fsLeft = data.freespinsLeft;
    }

    protected resetFeatureGameData():void {
        this._nudgeData = null;
        this.initValues();
    }

    /**
     * @override Overridding it to make sure keypad is not hidden for this screen.
     */
    protected show():boolean {
        this.startWinPresentation();

        if(this.shouldShow()) {
            this._view.show(this._serverData!, () => this.onShowComplete());
            SlotGame.keypad.hide();
            EventHandler.dispatchEvent(new GameEvent(BetLineEvent.SET_ENABLED, false));
            EventHandler.dispatchEvent(new GameEvent(BetLineEvent.HIDE_ALL_BET_LINES));
            EventHandler.dispatchEvent(new GameEvent(StageEvent.WANT_RESIZE));
            this._isShowing = true;
        }
        // When restore in freespin, don't show the intro but we need the intro to init the freespin scene.
        if(this.doTransitions) {
            this.doTransitions();
        }
        return this._isShowing;
    }

    protected startWinPresentation():void {
        BFScatterNudgeController._ScatterScreenStarted = true;     // added this to fix #180
        this.dimAllOtherSymbols(false);
        (this._nudgeData!.mode === BarbarianFuryGameMode.NORMAL) && EventHandler.dispatchEvent(new GameEvent("DIM_DOWN_WILD_MULTIPLIER")); // #260
        this._isNudging = true;
        let delay:number = this._nudgeData!.singleWin > 0 ? 0.5 : 0;
        this._nudgeData!.doScatterNudge.forEach((doNudge:boolean, reelId:number) => {
            if(doNudge) {
                this.doScatterReelNudge(reelId, false, delay);
                delay += (4 - this._scatterPositions![reelId]) * this.NUDGE_STEP_SPEED;
            }
        });
    }

    protected stopWinPresentation(isAbort:boolean):void {
        if(!isAbort) {
            return;
        }
        if(this._isNudging) {
            this._nudgeState.forEach((isNudging:boolean, reelId:number) => {
                if(isNudging) {
                    // Abort don't send complete event so it will not trigger onNudgeCompleted() again.
                    (<NudgeSpinReelController>Reels.getReel(reelId)).abortNudge();
                    this._nudgeState[reelId] = false;
                }
            });
        } else {
            this.dimAllOtherSymbols(true);
            this._nudgeData!.doScatterNudge.forEach((doNudge:boolean, reelId:number) => {
                if(doNudge) {
                    this.doScatterReelNudge(reelId, true, 0);
                }
            });
        }
        this._isNudging = false;
        SlotGame.keypad.setZeroBetSpinCounter(this._fsLeft);
        BFScatterNudgeController._ScatterScreenStarted = false;  // added this to fix #180
    }
}